import { cloneDeepWith, isEqual } from 'lodash'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import {
  SavedFilterData,
  SavedFilterUserPreference,
  UserPreferenceNamespace,
  UserPreferenceRequest,
  UserPreferenceType,
  useSavedFilterCreateMutation,
  useSavedFiltersDeleteMutation,
  useSavedFiltersListQuery
} from '~/api/userPreferences'
import { Msg, useMsg } from '~/common/localizationUtils'
import { useActionBarContext } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { useFilterValues, useRestoreSavedFilters } from '~/context/filters'
import { countFilterValues } from '~/context/filters/utils'
import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Stack,
  Text,
  TextProps,
  Tooltip,
  useDisclosure
} from '~/design_system'
import { ArrowLeft, Close } from '~/icons'
import { safeParse } from '~/common/dateUtils'
import { $thm } from '~/common/themeSx'

export function SavedFilterSection({ namespace }: { namespace: UserPreferenceNamespace }) {
  const { data: _data } = useSavedFiltersListQuery(namespace)
  const data = useMemo(
    () =>
      cloneDeepWith(_data, function fixupData(value, key) {
        // this is to parse the to and from date keys in date objects (ex. "end_date":{ "to": null/date obj, "from": null/date obj })
        if ((key === 'to' || key === 'from') && Boolean(value)) return safeParse(value)
        return undefined
      }),
    [_data]
  )
  const { isOpen, onOpen, onClose } = useDisclosure()
  const restoreSavedFilters = useRestoreSavedFilters()
  const { mutateAsync: deleteSavedFilter } = useSavedFiltersDeleteMutation()
  const actionBar = useActionBarContext()
  const filterValues = useFilterValues()
  const [selectedFiltersCount, setSelectedFilterCount] = useState<number>(0)
  const updateFilterCount = () => {
    /*
      eslint-disable-next-line react-hooks/set-state-in-effect --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Deriving selectedFiltersCount from filterValues causes extra render.
      Possible fix approach: Compute inline instead of state. memoization can be handled manually if needed:
        // Remove: const [selectedFiltersCount, setSelectedFilterCount] = useState<number>(0)
        // Remove: updateFilterCount function and useEffect
        const selectedFiltersCount = countFilterValues(filterValues)
    */
    setSelectedFilterCount(countFilterValues(filterValues))
  }
  // eslint-disable-next-line react-hooks/set-state-in-effect -- See updateFilterCount function for details
  useEffect(updateFilterCount, [filterValues])

  return (
    <Stack px={4} sx={$thm({ py: { $base: 4, $canvas: 0 }, mb: { $canvas: 4 } })}>
      {data?.length !== 0 && (
        <Text textStyle="bodySmall">
          <Msg id="components.filters.saved_filters_title" />
        </Text>
      )}
      {data
        ?.sort((a: SavedFilterUserPreference, b: SavedFilterUserPreference) => b.id - a.id)
        .map((savedFilter: SavedFilterUserPreference) => (
          <SavedFilter
            key={savedFilter.id}
            isSelected={areFiltersEqual(filterValues, savedFilter.data.filterValues)}
            onDelete={() => {
              deleteSavedFilter({ userPreferenceId: savedFilter.id })
            }}
            onRestore={() => {
              restoreSavedFilters(savedFilter.data.filterValues)
              actionBar.search?.onChange(savedFilter.data.search)
            }}
            dataTestId={`saved-filter-button-${savedFilter.data.name}`}>
            {savedFilter.data.name}
          </SavedFilter>
        ))}
      <Button
        onClick={onOpen}
        data-testid="filters-savebutton"
        variant="secondary"
        size="sm"
        disabled={selectedFiltersCount === 0}>
        <Msg id="components.filters.save_current_filters" count={selectedFiltersCount} />
      </Button>
      <SaveFiltersModal isOpen={isOpen} onClose={onClose} namespace={namespace} />
    </Stack>
  )
}

function areFiltersEqual(a: Record<string, any>, b: Record<string, any>) {
  return isEqual(cloneDeepWith(a, sortAllArrays), cloneDeepWith(b, sortAllArrays))

  function sortAllArrays(value: any) {
    if (Array.isArray(value)) return value.sort()
    return undefined
  }
}

export type SaveFilterModalProps = Pick<ModalProps, 'isOpen' | 'onClose'> & {
  namespace: UserPreferenceNamespace
}

function SaveFiltersModal({ isOpen, onClose, namespace }: SaveFilterModalProps) {
  const { mutateAsync: createSavedFilter } = useSavedFilterCreateMutation()
  const [savedFilterName, setSavedFilterName] = useState('')
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => setSavedFilterName(e.target.value)
  const filterValues = useFilterValues()
  const actionBar = useActionBarContext()
  const savedFilterData: SavedFilterData = {
    name: savedFilterName,
    filterValues,
    search: actionBar.search?.value
  }
  const userPreferenceData: UserPreferenceRequest = {
    namespace,
    preference_type: UserPreferenceType.SAVED_FILTERS,
    data: savedFilterData
  }
  const onSave = () => createSavedFilter(userPreferenceData)

  const onCloseActions = () => {
    onClose()
    setSavedFilterName('')
  }

  return (
    <Modal isOpen={isOpen} onClose={onCloseActions} size="lg">
      <ModalOverlay />
      <ModalContent>
        <Button variant="inlineLink" onClick={onCloseActions} width="20%" position="absolute" pt={2} pl={2}>
          <ArrowLeft mr={2} /> <Msg id="common.ui.back" description="Back" />
        </Button>
        <ModalHeader textAlign="center">
          <Msg id="save_filter_section.save_filters" description="Save Filters" />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired>
            <FormLabel>
              <Msg id="save_filter_section.saved_filter_name" description="Saved Filter Name" />
            </FormLabel>
            <Input
              id="savedFilterName"
              data-testid="filters-save-name-input"
              onChange={handleInputChange}
              maxLength={20}
            />
          </FormControl>
        </ModalBody>
        <Divider mt={3} />
        <ModalFooter justifyContent="flex-start">
          <Button
            variant="primary"
            mr={3}
            isDisabled={savedFilterName.trim().length === 0}
            data-testid="filters-save-submit-button"
            onClick={() => {
              onSave()
              onCloseActions()
            }}>
            <Msg id="save_filter_section.save_filters" description="Save Filters" />
          </Button>
          <Button onClick={onCloseActions}>
            <Msg id="common.ui.cancel" description="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

type SavedFilterButtonProps = {
  isSelected: boolean
  onDelete: () => void
  onRestore: () => void
  children: TextProps['children']
  dataTestId?: string
}

function SavedFilter({ isSelected, onDelete, onRestore, children, dataTestId }: SavedFilterButtonProps) {
  const msg = useMsg()
  return (
    <ButtonGroup
      size="sm"
      isAttached
      variant="ghost"
      backgroundColor={isSelected ? 'white' : undefined}
      borderLeftColor={isSelected ? 'blue.500' : 'transparent'}
      borderLeftWidth={4}
      pos="relative"
      justifyContent="space-between"
      sx={$thm({
        '&:hover .delete-saved-filter': {
          display: 'block'
        },
        left: { $base: -4, $canvas: 0 },
        width: { $base: 'calc(100% + 2rem)', $canvas: 'initial' }
      })}>
      <Tooltip label={children} openDelay={400}>
        <Button
          data-testid={dataTestId || 'saved-filter-button'}
          borderRadius={0}
          flexGrow={1}
          justifyContent="start"
          onClick={() => onRestore()}>
          <Text isTruncated textStyle="link">
            {children}
          </Text>
        </Button>
      </Tooltip>
      <IconButton
        className="delete-saved-filter"
        display="none"
        aria-label={msg('save_filter_section.delete_saved_filter')}
        borderRadius={0}
        icon={<Close w={5} h={5} p={0} m={0} onClick={onDelete} />}
      />
    </ButtonGroup>
  )
}
